import { clientFetch } from '~/lib/.client/fetch';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { getToken } from '~/lib/stores/token';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';

type ErrorResponse = {
  errors: Error[];
};

export function requestWithAuth(origin: string, needToken = true) {
  return async <T>(
    url: string,
    body: Record<any, any> | undefined,
    method: string,
    statusHandlers?: Record<number, (response: Response) => Promise<boolean> | boolean>,
  ) => {
    let responseText, status;

    try {
      const token = await getToken();

      if (!token && needToken) {
        return undefined;
      }

      const response = await clientFetch(new URL(url, origin).href, {
        headers: {
          'Content-Type': 'application/json',
          ...(needToken ? { authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
        method,
      });

      status = response.status;

      if (statusHandlers?.[response.status]) {
        const handled = await statusHandlers[response.status](response);

        if (handled) {
          return undefined;
        }
      }

      responseText = await response.text();

      if (response.ok && responseText.length === 0) {
        return undefined;
      }

      const responseJSON: T | ErrorResponse = JSON.parse(responseText);

      if (!response.ok) {
        if (Array.isArray((responseJSON as ErrorResponse).errors)) {
          throw new Error((responseJSON as ErrorResponse).errors.map((error) => error.message).join('\n'));
        }

        if (typeof responseJSON === 'object' && responseJSON) {
          if ('error' in responseJSON) {
            throw new Error(JSON.stringify(responseJSON.error));
          }

          throw new Error(JSON.stringify(responseJSON));
        }

        throw new Error(responseJSON as any);
      }

      return responseJSON as T;
    } catch (error: any) {
      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.RailsApiCallFailed,
          properties: {
            apiRoute: url,
            method,
            error: error.message,
            status,
            body: JSON.stringify(body),
            responseBody: responseText,
            stack: new Error().stack,
          },
        },
      });

      throw error;
    }
  };
}
